'use client';

import {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { css, cx } from 'styled-system/css';

import { Reveal } from '@/rsc/client';
import { Link as NextLink } from '@/rsc/ui/Link.client';
import { Text } from '@/rsc/ui/Text';

import {
  NodeKind,
  SegmentState,
  WhatWeDoRelations,
  WhatWeDoSelection,
  computeActiveSets,
  connectorPath,
  linkState,
  segmentState,
} from './what-we-do-geometry';

export type WhatWeDoNode = {
  id: string;
  label: string;
  description?: string;
  href?: string;
  /** Server-rendered icon (product cards). */
  icon?: ReactNode;
};

export type StackLink = { from: string; to: string };

type Props = {
  industries: WhatWeDoNode[];
  services: WhatWeDoNode[];
  products: WhatWeDoNode[];
  relations: WhatWeDoRelations;
  /** industry → service and service → product pairs, by id. */
  links: StackLink[];
  labels: {
    industries: string;
    services: string;
    products: string;
    core: string;
    captionDefault: string;
    captionRelated: string;
    learnMore: string;
  };
  /** Server-rendered S–H mark (the core node) and the ↗ arrow. */
  coreIcon: ReactNode;
  arrowIcon: ReactNode;
};

type StatefulNode = WhatWeDoNode & { state: SegmentState };
type NodeRect = { x: number; top: number; bottom: number };

// Hover debounce — sweeping the pointer across the stack shouldn't flicker every node it crosses.
const HOVER_ENTER_MS = 70;
const HOVER_LEAVE_MS = 120;
// One-shot attract loop when the stack scrolls in: industries light up, then services.
const DEMO_STEP_MS = 380;
const DEMO_ON_MS = 150;
const DEMO_OFF_MS = 560;

// ConnectionStack's `sx` block as one static Panda class (keyframes `sh-home-wwd-pulse` in globals.css).
const stackClass = css({
  position: 'relative',
  '--wwd-glass': 'rgba(255, 255, 255, 0.03)',
  '--wwd-glass-line': 'rgba(255, 255, 255, 0.07)',
  '--wwd-purple': 'rgba(147, 51, 234, 0.12)',
  '--wwd-purple-line': 'rgba(147, 51, 234, 0.55)',
  '--wwd-purple-glow': 'rgba(147, 51, 234, 0.25)',
  '--wwd-lime': '#D9FC59',
  '--wwd-lime-soft': 'rgba(217, 252, 89, 0.15)',
  '--wwd-lime-line': 'rgba(217, 252, 89, 0.5)',
  '--wwd-on-lime': '#120F19',

  '& .wwd-node': {
    position: 'relative',
    bg: 'var(--wwd-glass)',
    border: '1px solid var(--wwd-glass-line)',
    transition:
      'opacity 0.35s ease, background-color 0.35s ease, border-color 0.35s ease, transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 0.35s ease',
  },
  '& .wwd-node a, & .wwd-node [role="button"]': { outline: 'none', textDecoration: 'none' },
  '& .wwd-node:has(:focus-visible)': { borderColor: 'var(--wwd-purple-line)' },
  '& .wwd-node[data-state="dim"]': { opacity: 0.3 },
  '& .wwd-node[data-state="on"]': {
    bg: 'var(--wwd-purple)',
    borderColor: 'var(--wwd-purple-line)',
    transitionDelay: 'calc(var(--wwd-i, 0) * 40ms)',
  },
  '& .wwd-node[data-state="on"] .wwd-node-label': { color: '#FFFFFF' },
  '& .wwd-node[data-state="self"]': {
    bg: 'var(--wwd-lime)',
    borderColor: 'var(--wwd-lime)',
    transform: 'translateY(-3px)',
    boxShadow: '0 16px 48px rgba(217, 252, 89, 0.22)',
  },
  '& .wwd-node[data-state="self"] .wwd-node-label, & .wwd-node[data-state="self"] .wwd-node-desc': {
    color: 'var(--wwd-on-lime)',
  },
  '& .wwd-node[data-state="self"] .wwd-node-icon': { color: 'var(--wwd-on-lime)' },
  '& .wwd-node[data-state="self"] .wwd-node-chip': {
    bg: 'rgba(18, 15, 25, 0.1)',
    borderColor: 'rgba(18, 15, 25, 0.2)',
  },
  '& .wwd-node[data-state="self"] .wwd-node-arrow, & .wwd-node[data-state="on"] .wwd-node-arrow': {
    opacity: 1,
    transform: 'none',
  },
  '& .wwd-node[data-state="self"] .wwd-node-arrow': { color: 'var(--wwd-on-lime)' },
  '@media (hover: hover) and (pointer: fine)': {
    '& .wwd-node[data-state="idle"]:hover': { borderColor: 'var(--wwd-purple-line)' },
  },

  '& .wwd-link': {
    fill: 'none',
    stroke: 'rgba(255, 255, 255, 0.08)',
    strokeWidth: 1,
    transition: 'stroke 0.35s ease, opacity 0.35s ease, stroke-width 0.35s ease',
  },
  '& .wwd-link[data-state="dim"]': { opacity: 0.25 },
  '& .wwd-link[data-state="on"]': {
    stroke: 'var(--wwd-purple-line)',
    strokeWidth: 1.25,
    transitionDelay: 'calc(var(--wwd-i, 0) * 25ms)',
  },
  '& .wwd-link[data-state="self"]': { stroke: 'var(--wwd-lime)', strokeWidth: 1.5, filter: 'url(#wwd-link-glow)' },

  '& .wwd-core': {
    cursor: 'pointer',
    outline: 'none',
    position: 'relative',
    bg: 'var(--wwd-glass)',
    border: '1px solid var(--wwd-glass-line)',
    transition: 'background-color 0.35s ease, border-color 0.35s ease',
  },
  '& .wwd-core svg': { color: 'rgba(217, 252, 89, 0.9)', transition: 'color 0.35s ease' },
  '& .wwd-core:hover, & .wwd-core:focus-visible, & .wwd-core[data-state="on"]': {
    bg: 'var(--wwd-purple)',
    borderColor: 'var(--wwd-purple-line)',
  },
  '& .wwd-core:hover svg, & .wwd-core:focus-visible svg, & .wwd-core[data-state="on"] svg': {
    color: 'var(--wwd-lime)',
  },
  '& .wwd-core-pulse': {
    position: 'absolute',
    inset: '-1px',
    borderRadius: 'inherit',
    border: '1.5px solid var(--wwd-lime)',
    opacity: 0,
    pointerEvents: 'none',
  },
  '& .wwd-core[data-state="on"] .wwd-core-pulse': { animation: 'sh-home-wwd-pulse 1.6s ease-out infinite' },

  '@media (prefers-reduced-motion: reduce)': {
    '& .wwd-node[data-state="self"]': { transform: 'none' },
    '& .wwd-core[data-state="on"] .wwd-core-pulse': { animation: 'none' },
  },
});

const svgClass = css({
  position: 'absolute',
  inset: 0,
  w: '100%',
  h: '100%',
  pointerEvents: 'none',
  display: { base: 'none', lg: 'block' },
});

const bandsClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: { base: 8, lg: '72px' },
  position: 'relative',
});
const bandLabelClass = css({
  display: 'block',
  fontFamily: 'mono',
  fontSize: 'xs',
  fontWeight: '600',
  letterSpacing: 'widest',
  color: 'whiteAlpha.600',
  textTransform: 'uppercase',
  mb: 3,
});
const chipListClass = css({ display: 'flex', listStyleType: 'none', m: 0, p: 0, flexWrap: 'wrap', gap: 3 });
const coreClass = css({
  w: '56px',
  h: '56px',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});
const chipClass = css({ borderRadius: '10px' });
const chipLabelClass = {
  plain: css({
    fontFamily: 'body',
    fontSize: 'sm',
    fontWeight: 500,
    lineHeight: '5',
    color: 'whiteAlpha.800',
    transition: 'color 0.35s ease',
    whiteSpace: 'nowrap',
  }),
  emphasis: css({
    fontFamily: 'heading',
    fontSize: 'md',
    fontWeight: 600,
    lineHeight: '5',
    color: 'whiteAlpha.800',
    transition: 'color 0.35s ease',
    whiteSpace: 'nowrap',
  }),
};
const chipButtonClass = {
  plain: css({ px: 4, py: '10px', cursor: 'pointer' }),
  emphasis: css({ px: '18px', py: 3, cursor: 'pointer' }),
};
const productGridClass = css({
  display: 'grid',
  listStyleType: 'none',
  m: 0,
  p: 0,
  gridTemplateColumns: {
    base: 'repeat(1, minmax(0, 1fr))',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(5, minmax(0, 1fr))',
  },
  gap: 3,
});
const productChipClass = css({
  display: 'flex',
  w: '40px',
  h: '40px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  bg: 'var(--wwd-lime-soft)',
  border: '1px solid var(--wwd-lime-line)',
  transition: 'background-color 0.35s ease, border-color 0.35s ease',
});
const productNumberClass = css({
  fontFamily: 'mono',
  fontSize: 'xs',
  fontWeight: '600',
  letterSpacing: 'widest',
  color: 'whiteAlpha.600',
});
const productLabelClass = css({
  display: 'block',
  fontFamily: 'heading',
  fontSize: 'lg',
  fontWeight: '600',
  lineHeight: '7',
  color: 'text.500',
  transition: 'color 0.35s ease',
});
const productArrowClass = css({
  display: 'inline-flex',
  alignSelf: 'flex-end',
  opacity: 0,
  transform: 'translate(-6px, 6px)',
  transition: 'opacity 0.35s ease, transform 0.35s ease',
  color: 'var(--wwd-lime)',
  '& svg': { w: 4, h: 4 },
});

// Touch and small screens: the connectors are hidden, so the caption carries the relations.
// v2: display { base: block, lg: isTouch ? block : none } — `(hover: none)` is the same test the
// original's isTouch used, so CSS decides without a re-render.
const captionClass = css({
  mt: 6,
  display: 'block',
  minH: '72px',
  '@media (min-width: 80em) and (hover: hover)': { display: 'none', minH: 0 },
});

const toSelection = (kind: NodeKind, id: string): WhatWeDoSelection => ({ kind, id });

/**
 * Client island of the homepage "What we do" map: owns the selection (hover with debounce, tap on
 * touch, keyboard), the one-shot attract loop, the measured SVG connectors and the aria-live
 * caption. Everything else in the section (eyebrow, heading, subtitle, hints) is server-rendered.
 * Merges WhatWeDoMapContent's state with the presentational ConnectionStack.
 */
export const WhatWeDoStack = ({
  industries,
  services,
  products,
  relations,
  links,
  labels,
  coreIcon,
  arrowIcon,
}: Props) => {
  const [selection, setSelection] = useState<WhatWeDoSelection | null>(null);
  const [isTouch, setIsTouch] = useState(false);
  const [demoBand, setDemoBand] = useState<'industry' | 'service' | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const stackRef = useRef<HTMLDivElement | null>(null);
  const demoDone = useRef(false);
  const selectionRef = useRef<WhatWeDoSelection | null>(null);
  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  useEffect(() => {
    setIsTouch(window.matchMedia('(hover: none)').matches);
  }, []);

  const onHover = useCallback((next: WhatWeDoSelection | null) => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setSelection(next), next ? HOVER_ENTER_MS : HOVER_LEAVE_MS);
  }, []);

  const onSelect = useCallback((next: WhatWeDoSelection | null) => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    setSelection(next);
  }, []);

  useEffect(
    () => () => {
      if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    },
    [],
  );

  // Attract loop, once, when ~half the stack is visible and nothing is selected.
  useEffect(() => {
    const el = stackRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timers: number[] = [];
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || demoDone.current) return;
        demoDone.current = true;
        observer.disconnect();
        (['industry', 'service'] as const).forEach((band, i) => {
          timers.push(
            window.setTimeout(() => {
              if (!selectionRef.current) setDemoBand(band);
            }, DEMO_ON_MS + i * DEMO_STEP_MS),
          );
          timers.push(window.setTimeout(() => setDemoBand(null), DEMO_OFF_MS + i * DEMO_STEP_MS));
        });
      },
      { threshold: 0.45 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const active = useMemo(() => computeActiveSets(selection, relations), [selection, relations]);

  const industryNodes: StatefulNode[] = industries.map((node) => ({
    ...node,
    state: demoBand === 'industry' && !selection ? 'on' : segmentState('industry', node.id, selection, active),
  }));
  const serviceNodes: StatefulNode[] = services.map((node) => ({
    ...node,
    state: demoBand === 'service' && !selection ? 'on' : segmentState('service', node.id, selection, active),
  }));
  const productNodes: StatefulNode[] = products.map((node) => ({
    ...node,
    state: segmentState('product', node.id, selection, active),
  }));

  const allNodes = [...industryNodes, ...serviceNodes, ...productNodes];
  const selectedNode =
    selection && selection.kind !== 'core' ? allNodes.find((node) => node.id === selection.id) : undefined;
  const selectedLabel = selection?.kind === 'core' ? labels.core : selectedNode?.label ?? null;
  const selectedHref = selectedNode?.href;
  const relatedLabels = active ? allNodes.filter((node) => node.state === 'on').map((node) => node.label) : [];
  const coreState = selection?.kind === 'core' || selection?.kind === 'product' ? 'on' : 'idle';
  const hasSelection = !!selection;

  // --- connector geometry (ConnectionStack) ---
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nodeEls = useRef(new Map<string, HTMLElement>());
  const [layout, setLayout] = useState<{ width: number; height: number; rects: Record<string, NodeRect> } | null>(null);

  const registerNode = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) nodeEls.current.set(id, el);
      else nodeEls.current.delete(id);
    },
    [],
  );

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const base = container.getBoundingClientRect();
    const rects: Record<string, NodeRect> = {};
    nodeEls.current.forEach((el, id) => {
      const r = el.getBoundingClientRect();
      rects[id] = { x: r.left - base.left + r.width / 2, top: r.top - base.top, bottom: r.bottom - base.top };
    });
    setLayout({ width: base.width, height: base.height, rects });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    // Fires once on observe (initial measure) and again whenever the stack reflows.
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  const stateOf = (id: string): SegmentState => allNodes.find((n) => n.id === id)?.state ?? 'idle';
  const onCoreActivate = () => onSelect(selection?.kind === 'core' ? null : { kind: 'core' });

  const chipProps = { isTouch, selection, onHover, onSelect, registerNode };

  return (
    <>
      <div ref={stackRef}>
        <Reveal>
          <div ref={containerRef} className={stackClass}>
            {/* Connectors: measured from the chips, drawn underneath them. Hidden while chips wrap. */}
            {layout && (
              <svg
                aria-hidden
                className={svgClass}
                viewBox={`0 0 ${layout.width} ${layout.height}`}
                preserveAspectRatio="none"
              >
                <defs>
                  <filter id="wwd-link-glow" x="-10%" y="-10%" width="120%" height="120%">
                    <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#D9FC59" floodOpacity="0.5" />
                  </filter>
                </defs>
                {links.map(({ from, to }, index) => {
                  const a = layout.rects[from];
                  const b = layout.rects[to];
                  if (!a || !b) return null;
                  return (
                    <path
                      key={`${from}-${to}`}
                      className="wwd-link"
                      data-state={linkState(stateOf(from), stateOf(to), hasSelection)}
                      style={{ '--wwd-i': index % 12 } as CSSProperties}
                      d={connectorPath(a.x, a.bottom, b.x, b.top)}
                    />
                  );
                })}
              </svg>
            )}

            <div className={bandsClass}>
              {/* Industries */}
              <div>
                <span className={bandLabelClass}>{labels.industries}</span>
                <ul className={chipListClass}>
                  {industryNodes.map((node, index) => (
                    <Chip key={node.id} kind="industry" node={node} index={index} {...chipProps} />
                  ))}
                </ul>
              </div>

              {/* Services + S–H node */}
              <div>
                <span className={bandLabelClass}>{labels.services}</span>
                <div className={css({ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' })}>
                  <div
                    className={cx('wwd-core', coreClass)}
                    role="button"
                    tabIndex={0}
                    aria-label={labels.core}
                    aria-pressed={selection?.kind === 'core'}
                    data-state={coreState}
                    onMouseEnter={() => onHover({ kind: 'core' })}
                    onMouseLeave={() => onHover(null)}
                    onFocus={() => onHover({ kind: 'core' })}
                    onBlur={() => onHover(null)}
                    onClick={onCoreActivate}
                    onKeyDown={(event: KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onCoreActivate();
                      }
                    }}
                  >
                    <div className="wwd-core-pulse" aria-hidden />
                    {coreIcon}
                  </div>
                  <ul className={cx(chipListClass, css({ flex: 1 }))}>
                    {serviceNodes.map((node, index) => (
                      <Chip key={node.id} kind="service" node={node} index={index} emphasis {...chipProps} />
                    ))}
                  </ul>
                </div>
              </div>

              {/* AI products */}
              <div>
                <span className={bandLabelClass}>{labels.products}</span>
                <ul className={productGridClass}>
                  {productNodes.map((node, index) => {
                    const sel = toSelection('product', node.id);
                    return (
                      <li
                        key={node.id}
                        ref={registerNode(node.id)}
                        className="wwd-node"
                        data-state={node.state}
                        style={{ '--wwd-i': index } as CSSProperties}
                        onMouseEnter={() => onHover(sel)}
                        onMouseLeave={() => onHover(null)}
                      >
                        <NextLink
                          href={node.href ?? '#'}
                          prefetch={false}
                          onFocus={() => onHover(sel)}
                          onBlur={() => onHover(null)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            padding: '20px',
                            height: '100%',
                          }}
                        >
                          <span
                            className={css({
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            })}
                          >
                            <span className={cx('wwd-node-chip', productChipClass)}>{node.icon}</span>
                            <span className={productNumberClass}>{String(index + 1).padStart(2, '0')}</span>
                          </span>
                          <span className={css({ display: 'block', flex: 1 })}>
                            <span className={cx('wwd-node-label', productLabelClass)}>{node.label}</span>
                            {node.description && (
                              <Text
                                as="span"
                                variant="text-sm/lineHeight-5/font-normal"
                                className={cx(
                                  'wwd-node-desc',
                                  css({
                                    display: 'block',
                                    color: 'whiteAlpha.600',
                                    mt: 1,
                                    transition: 'color 0.35s ease',
                                  }),
                                )}
                              >
                                {node.description}
                              </Text>
                            )}
                          </span>
                          <span className={cx('wwd-node-arrow', productArrowClass)} aria-hidden>
                            {arrowIcon}
                          </span>
                        </NextLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <div aria-live="polite" className={captionClass}>
        {selectedLabel ? (
          <>
            <Text variant="text-lg/lineHeight-7/font-medium" className={css({ color: 'text.500' })}>
              {selectedLabel}
            </Text>
            {relatedLabels.length > 0 && (
              <Text variant="text-sm/lineHeight-5/font-normal" className={css({ color: 'whiteAlpha.600', mt: 1 })}>
                {labels.captionRelated}: {relatedLabels.join(' · ')}
              </Text>
            )}
            {selectedHref && (
              <NextLink href={selectedHref} prefetch={false}>
                <span
                  className={css({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2,
                    color: 'rgba(217,252,89,0.85)',
                    '& svg': { w: 4, h: 4 },
                  })}
                >
                  <Text as="span" variant="text-sm/lineHeight-5/font-medium">
                    {labels.learnMore}
                  </Text>
                  {arrowIcon}
                </span>
              </NextLink>
            )}
          </>
        ) : (
          <Text variant="text-sm/lineHeight-5/font-normal" className={css({ color: 'whiteAlpha.600' })}>
            {labels.captionDefault}
          </Text>
        )}
      </div>
    </>
  );
};

type ChipProps = {
  kind: 'industry' | 'service';
  node: StatefulNode;
  index: number;
  isTouch: boolean;
  selection: WhatWeDoSelection | null;
  onHover: (selection: WhatWeDoSelection | null) => void;
  onSelect: (selection: WhatWeDoSelection | null) => void;
  registerNode: (id: string) => (el: HTMLElement | null) => void;
  emphasis?: boolean;
};

const Chip = ({ kind, node, index, isTouch, selection, onHover, onSelect, registerNode, emphasis }: ChipProps) => {
  const sel = toSelection(kind, node.id);
  const isSelected = !!selection && selection.kind === kind && 'id' in selection && selection.id === node.id;
  const toggle = () => onSelect(isSelected ? null : sel);
  const variant = emphasis ? 'emphasis' : 'plain';
  const label = <span className={cx('wwd-node-label', chipLabelClass[variant])}>{node.label}</span>;

  return (
    <li
      ref={registerNode(node.id)}
      className={cx('wwd-node', chipClass)}
      data-state={node.state}
      style={{ '--wwd-i': index } as CSSProperties}
      onMouseEnter={() => onHover(sel)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(sel)}
      onBlur={() => onHover(null)}
    >
      {node.href && !isTouch ? (
        <NextLink
          href={node.href}
          prefetch={false}
          style={{ display: 'block', padding: emphasis ? '12px 18px' : '10px 16px' }}
        >
          {label}
        </NextLink>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-pressed={isSelected}
          className={chipButtonClass[variant]}
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            toggle();
          }}
          onKeyDown={(event: KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggle();
            }
          }}
        >
          {label}
        </div>
      )}
    </li>
  );
};
